import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Music4, Play, Square, Trash2, Download, Plus, Eraser, Save } from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";

/* ─────────────────────────────────────────────────────────────────────────
 *  MIDI Clip Editor — pianoroll for composing inline melodies.
 *
 *  Notes are stored as `{id, pitch, startBeat, durationBeats, velocity}`.
 *  - pitch: MIDI note number (48 = C3, 60 = C4 = middle C)
 *  - startBeat: float beats since clip start (0 = first beat)
 *  - durationBeats: float beats long (snapped to 0.25 beat = 16th note)
 *  - velocity: 1-127, drives the synth peak gain
 *
 *  Rendering uses the engine's `renderMidiClipToBuffer` which runs an
 *  OfflineAudioContext synth pass — no network round-trip. Result loads
 *  onto the first empty channel.
 *  ───────────────────────────────────────────────────────────────────────── */

const VISIBLE_OCTAVES = 4;   // C2 (36) → C6 (84). Enough range for most melodies.
const LOW_PITCH = 36;        // C2
const HIGH_PITCH = LOW_PITCH + VISIBLE_OCTAVES * 12;  // 84 = C6
const ROW_H = 14;            // px per pitch row
const BEAT_W = 56;           // px per beat
const SNAP = 0.25;           // 16th-note snap

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const BLACK_KEYS = new Set([1, 3, 6, 8, 10]);

function midiToName(m) {
  const n = NOTE_NAMES[m % 12];
  const oct = Math.floor(m / 12) - 1;
  return `${n}${oct}`;
}
function isBlackKey(pitch) { return BLACK_KEYS.has(pitch % 12); }

let _nid = 1;
function newNoteId() { return `n${_nid++}`; }

/** Snap a beat value to the nearest grid line. */
function snap(beat) { return Math.round(beat / SNAP) * SNAP; }

export default function MidiClipEditor({ open, onClose, engine, onClipRendered }) {
  useEscapeClose(open, onClose);
  // Clip-level state
  const [bars, setBars] = useState(4);
  const [beatsPerBar] = useState(4);
  const [tempo, setTempo] = useState(() => engine?.tempo || 120);
  const [waveform, setWaveform] = useState("sawtooth");
  const [notes, setNotes] = useState([]);
  // Render mode: "replace" (legacy — drops into first empty channel as primary
  // buffer) vs "clip" (chapter-c — adds to the SELECTED channel as a draggable
  // clip at beat 0, alongside any existing content).
  const [renderMode, setRenderMode] = useState("clip");
  const [targetChannel, setTargetChannel] = useState(0);
  // Playback
  const [playing, setPlaying] = useState(false);
  const playStartedAtRef = useRef(0);
  const playRafRef = useRef(null);
  const [playheadBeat, setPlayheadBeat] = useState(0);

  const totalBeats = bars * beatsPerBar;
  const gridWidth = totalBeats * BEAT_W;
  const rowsCount = HIGH_PITCH - LOW_PITCH + 1;
  const gridHeight = rowsCount * ROW_H;

  // Reset on modal open so the editor is always a clean slate.
  useEffect(() => {
    if (!open) return;
    setNotes([]);
    setPlaying(false);
    setPlayheadBeat(0);
    setTempo(engine?.tempo || 120);
  }, [open, engine]);

  // Stop the rAF loop on unmount.
  useEffect(() => () => {
    if (playRafRef.current) cancelAnimationFrame(playRafRef.current);
  }, []);

  /** Add a note at (rowIdx, beat). Called from the grid background click.
   *  rowIdx counts FROM THE TOP (so 0 = highest pitch we render). */
  const addNoteAt = useCallback((rowIdx, beat) => {
    const pitch = HIGH_PITCH - rowIdx;
    const startBeat = Math.max(0, snap(beat));
    const note = {
      id: newNoteId(),
      pitch,
      startBeat,
      durationBeats: 1,  // one beat default
      velocity: 100,
    };
    setNotes((prev) => [...prev, note]);
    // Preview the pitch immediately so it's musically obvious what was added.
    try { engine?.previewMidiNote?.(pitch, 0.3, 100, waveform); } catch (e) { void e; }
  }, [engine, waveform]);

  const deleteNote = useCallback((id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    if (!notes.length) return;
    setNotes([]);
    toast.message("Notes cleared");
  }, [notes.length]);

  // ─── Playback ─────────────────────────────────────────────────────────
  const stopPlayback = useCallback(() => {
    if (playRafRef.current) cancelAnimationFrame(playRafRef.current);
    playRafRef.current = null;
    setPlaying(false);
    setPlayheadBeat(0);
  }, []);

  const startPlayback = useCallback(() => {
    if (!engine || !notes.length) {
      toast.message("Add some notes first");
      return;
    }
    if (engine.ctx?.state === "suspended") {
      try { engine.ctx.resume(); } catch (e) { void e; }
    }
    const ctx = engine.ctx;
    const secPerBeat = 60 / tempo;
    const startTime = ctx.currentTime + 0.05;
    playStartedAtRef.current = startTime;
    // Schedule every note via the engine's synth.
    notes.forEach((n) => {
      const t = startTime + n.startBeat * secPerBeat;
      const dur = Math.max(0.02, n.durationBeats * secPerBeat);
      engine._scheduleSynthNote(ctx, ctx.destination, n.pitch, t, dur, n.velocity, waveform);
    });
    setPlaying(true);
    // Animate the playhead via rAF — translates ctx.currentTime → beats.
    const tick = () => {
      const elapsed = ctx.currentTime - playStartedAtRef.current;
      const beat = elapsed / secPerBeat;
      if (beat >= totalBeats + 0.1) {
        stopPlayback();
        return;
      }
      setPlayheadBeat(Math.max(0, beat));
      playRafRef.current = requestAnimationFrame(tick);
    };
    playRafRef.current = requestAnimationFrame(tick);
  }, [engine, notes, tempo, totalBeats, waveform, stopPlayback]);

  const handleGridClick = useCallback((e) => {
    const grid = e.currentTarget;
    const rect = grid.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const rowIdx = Math.floor(y / ROW_H);
    const beat = x / BEAT_W;
    if (rowIdx < 0 || rowIdx >= rowsCount) return;
    if (beat < 0 || beat > totalBeats) return;
    addNoteAt(rowIdx, beat);
  }, [addNoteAt, rowsCount, totalBeats]);

  /** Render the clip to an AudioBuffer + load into the first empty channel.
   *  This is the "export" path — the buffer is treated as a regular sample,
   *  so all FX / automation / mixing applies normally. */
  const handleRender = useCallback(async () => {
    if (!engine) return;
    if (!notes.length) {
      toast.error("No notes to render", { description: "Click somewhere on the grid first." });
      return;
    }
    try {
      const buf = await engine.renderMidiClipToBuffer({
        notes, tempo, beatsPerBar, bars, waveform,
      });
      if (renderMode === "clip") {
        // Chapter-c path — append as a draggable clip on the chosen channel.
        const id = engine.addClipToChannel?.(
          targetChannel, buf, 0,
          `MIDI · ${notes.length}n · ${bars}b · ${tempo}bpm`,
          bars * beatsPerBar
        );
        if (!id) {
          toast.error("Could not add clip", { description: "Channel index out of range." });
          return;
        }
        toast.success("Clip added to channel " + (targetChannel + 1), {
          description: "Open Arrangement (Cmd+K → arrangement) to drag it.",
        });
        onClipRendered?.(targetChannel);
        onClose?.();
      } else {
        // Replace-mode — legacy behaviour, find first empty channel.
        let target = -1;
        for (let i = 0; i < (engine.channels?.length || 0); i++) {
          if (!engine.channels[i]?.buffer) { target = i; break; }
        }
        if (target < 0) {
          toast.error("Mixer is full", { description: "Clear a channel first or switch to 'Add as clip'." });
          return;
        }
        engine.loadBufferToChannel(target, buf, `MIDI Clip · ${tempo}bpm · ${notes.length} notes`);
        toast.success("Rendered to channel " + (target + 1), {
          description: `${notes.length} notes · ${bars} bars · ${tempo} bpm`,
        });
        onClipRendered?.(target);
        onClose?.();
      }
    } catch (e) {
      toast.error("Render failed", { description: e?.message || "Unknown error" });
    }
  }, [engine, notes, tempo, beatsPerBar, bars, waveform, renderMode, targetChannel, onClipRendered, onClose]);

  // Pre-compute row metadata. We render from highest pitch at top → lowest at bottom.
  // MUST live above the early-return below so hooks fire in the same order each render.
  const rows = useMemo(() => {
    const out = [];
    for (let i = 0; i < rowsCount; i++) {
      const pitch = HIGH_PITCH - i;
      out.push({ pitch, isBlack: isBlackKey(pitch), name: midiToName(pitch), idx: i });
    }
    return out;
  }, [rowsCount]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="midi-editor-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-[min(96vw,1100px)] max-h-[92vh] overflow-hidden rounded-3xl border border-white/[0.1] bg-gradient-to-br from-zinc-900/95 to-zinc-950/95 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.65)]"
          data-testid="midi-editor-modal"
        >
          {/* Header */}
          <header className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-6 py-4">
            <div className="flex items-center gap-3">
              <Music4 className="h-5 w-5 text-fuchsia-300" />
              <div>
                <h2 className="font-display text-[18px] font-semibold tracking-tight text-white">MIDI Clip Editor</h2>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Click the grid to add notes · play to preview · render to channel
                </p>
              </div>
            </div>
            <button
              type="button"
              data-testid="midi-editor-close-btn"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/65 transition hover:bg-white/[0.1]"
              aria-label="Close MIDI editor"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 border-b border-white/[0.07] bg-white/[0.02] px-6 py-3">
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
              Bars
              <select
                data-testid="midi-editor-bars-select"
                value={bars}
                onChange={(e) => setBars(Number(e.target.value))}
                className="rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white"
              >
                {[1, 2, 4, 8, 16].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
              Tempo
              <input
                type="number"
                data-testid="midi-editor-tempo-input"
                min={40}
                max={240}
                value={tempo}
                onChange={(e) => setTempo(Math.max(40, Math.min(240, Number(e.target.value) || 120)))}
                className="w-16 rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white"
              />
              <span className="font-mono text-[10px] text-white/40">BPM</span>
            </label>
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
              Voice
              <select
                data-testid="midi-editor-waveform-select"
                value={waveform}
                onChange={(e) => setWaveform(e.target.value)}
                className="rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white"
              >
                <option value="sawtooth">Saw lead</option>
                <option value="square">Square</option>
                <option value="triangle">Triangle</option>
                <option value="sine">Sine pad</option>
              </select>
            </label>
            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
              On render
              <select
                data-testid="midi-editor-render-mode-select"
                value={renderMode}
                onChange={(e) => setRenderMode(e.target.value)}
                className="rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white"
              >
                <option value="clip">Add as clip</option>
                <option value="replace">Replace channel</option>
              </select>
            </label>
            {renderMode === "clip" ? (
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
                On channel
                <select
                  data-testid="midi-editor-target-channel-select"
                  value={targetChannel}
                  onChange={(e) => setTargetChannel(Number(e.target.value))}
                  className="w-16 rounded-md border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white"
                >
                  {Array.from({ length: (engine?.channels?.length || 64) }).slice(0, 16).map((_, i) => (
                    <option key={i} value={i}>{i + 1}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="flex-1" />
            <span data-testid="midi-editor-note-count" className="font-mono text-[10px] uppercase tracking-wider text-white/45">
              {notes.length} note{notes.length === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              data-testid="midi-editor-clear-btn"
              onClick={clearAll}
              disabled={!notes.length}
              className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[9.5px] uppercase tracking-wider text-white/65 transition hover:border-rose-400/30 hover:bg-rose-500/15 hover:text-rose-100 disabled:opacity-40"
            >
              <Eraser className="h-3 w-3" />Clear
            </button>
            {playing ? (
              <button
                type="button"
                data-testid="midi-editor-stop-btn"
                onClick={stopPlayback}
                className="flex items-center gap-1.5 rounded-xl bg-rose-500/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-rose-100 ring-1 ring-rose-400/50 transition hover:bg-rose-500/45"
              >
                <Square className="h-3.5 w-3.5" />Stop
              </button>
            ) : (
              <button
                type="button"
                data-testid="midi-editor-play-btn"
                onClick={startPlayback}
                disabled={!notes.length}
                className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-500/85 to-sky-500/85 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-white transition hover:from-emerald-500 hover:to-sky-500 disabled:opacity-40"
              >
                <Play className="h-3.5 w-3.5" />Preview
              </button>
            )}
            <button
              type="button"
              data-testid="midi-editor-render-btn"
              onClick={handleRender}
              disabled={!notes.length}
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-fuchsia-500/85 to-rose-500/85 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-white transition hover:from-fuchsia-500 hover:to-rose-500 disabled:opacity-40"
            >
              <Save className="h-3.5 w-3.5" />Render → channel
            </button>
          </div>

          {/* Pianoroll grid — left key column + scrollable grid */}
          <div className="flex max-h-[64vh] overflow-auto" data-testid="midi-editor-pianoroll">
            {/* Key column (sticky-ish via left position) */}
            <div className="flex-shrink-0 border-r border-white/[0.07] bg-black/40" style={{ width: 56 }}>
              {rows.map((r) => (
                <button
                  key={`key-${r.pitch}`}
                  type="button"
                  data-testid={`midi-editor-key-${r.pitch}`}
                  onClick={() => engine?.previewMidiNote?.(r.pitch, 0.4, 100, waveform)}
                  className={`flex w-full items-center justify-end pr-2 font-mono text-[8.5px] uppercase tracking-wider transition ${
                    r.isBlack
                      ? "bg-zinc-900/80 text-white/45 hover:bg-zinc-800"
                      : "bg-zinc-700/40 text-white/70 hover:bg-zinc-600/50"
                  } ${r.name === "C4" ? "ring-1 ring-fuchsia-400/50" : ""}`}
                  style={{ height: ROW_H }}
                  title={r.name}
                >
                  {r.name}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div
              data-testid="midi-editor-grid"
              onClick={handleGridClick}
              className="relative cursor-crosshair"
              style={{ width: gridWidth, height: gridHeight }}
            >
              {/* Background — rows alternate black-key tint */}
              {rows.map((r) => (
                <div
                  key={`row-${r.pitch}`}
                  className={r.isBlack ? "bg-white/[0.015]" : "bg-transparent"}
                  style={{ height: ROW_H, width: gridWidth, position: "absolute", top: r.idx * ROW_H, left: 0 }}
                />
              ))}
              {/* Vertical beat lines */}
              {Array.from({ length: totalBeats + 1 }).map((_, b) => (
                <div
                  key={`bar-${b}`}
                  className={`absolute top-0 ${b % beatsPerBar === 0 ? "bg-white/[0.18]" : "bg-white/[0.06]"}`}
                  style={{ left: b * BEAT_W, width: 1, height: gridHeight }}
                />
              ))}
              {/* Note blocks */}
              {notes.map((n) => {
                const rowIdx = HIGH_PITCH - n.pitch;
                const top = rowIdx * ROW_H + 1;
                const left = n.startBeat * BEAT_W;
                const width = Math.max(8, n.durationBeats * BEAT_W - 2);
                return (
                  <div
                    key={n.id}
                    data-testid={`midi-editor-note-${n.id}`}
                    onClick={(e) => { e.stopPropagation(); deleteNote(n.id); }}
                    title={`${midiToName(n.pitch)} · ${n.startBeat.toFixed(2)} beats · click to delete`}
                    className="absolute cursor-pointer rounded-sm bg-gradient-to-r from-fuchsia-500 to-rose-400 ring-1 ring-fuchsia-300/60 transition hover:from-rose-400 hover:to-amber-400"
                    style={{ top, left, width, height: ROW_H - 2 }}
                  />
                );
              })}
              {/* Playhead */}
              {playing ? (
                <div
                  data-testid="midi-editor-playhead"
                  className="pointer-events-none absolute top-0 z-10 w-[2px] bg-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.7)]"
                  style={{ left: playheadBeat * BEAT_W, height: gridHeight }}
                />
              ) : null}
            </div>
          </div>

          {/* Tip footer */}
          <div className="border-t border-white/[0.07] bg-black/30 px-6 py-2 font-mono text-[10px] uppercase tracking-wider text-white/40">
            Tip · click a piano key to audition the pitch · click an empty grid cell to drop a note · click a note to delete
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
